import React from "react";
import classes from "../Style.css"
import DocPage from "../DocPage/DocPage"
import { NavLink } from "react-router-dom"
import version from "./version-selector.css"

import Figure2 from "./Figures/2.png"
import Figure3 from "./Figures/3.png"
import Figure4 from "./Figures/4.png"
import Figure5 from "./Figures/5.png"
import Figure6 from "./Figures/6.png"

const DocumentationPage = (props) => {

  return (
    <>
      <DocPage title={"Using the BACnet protocol"} link={"https://gateway.allorado.io/docs/protocol-gateway/10.%20Using%20BACnet.pdf"}>

        <div className={version.container}>
          <NavLink className={version.item} to={"/docs/using-bacnet-before-1.7"}>1.6 and earlier</NavLink>
          <div className={version.selectedItem}>1.7 and later</div>
        </div>

        <div className={classes.docContainer}>

          <p>The Allorado Gateway simplifies the integration of LoRa and BACnet like never before. It offers multiple options for working with BACnet, each tailored to different use cases:</p>
          <ol>
            <li>An automatic “LoRa to BACnet” mapper available via the web interface</li>
            <li>A dedicated “BACnet Objects” node for use in NodeRED</li>
          </ol>

          <h3>1. Web interface vs. NodeRED</h3>
          <p>The web interface includes an automatic mapper that links LoRa devices to BACnet. For each provisioned LoRa device, you can choose which values to expose to BACnet — for example, selecting the temperature reading of a sensor while excluding its humidity. Once configured, the dataflow is handled automatically, with no further setup required.</p>
          <p>The NodeRED option provides greater flexibility and control, though it requires more manual configuration. With the “BACnet Objects” node, users can create custom flows and interact with BACnet objects alongside other protocols and nodes. For example, by connecting the BACnet node to a LoRa Downlink node, you can switch a LoRa-enabled smart plug relay by toggling a BACnet binary value.</p>

          <h3>2. Using the web interface</h3>

          <img style={{ marginTop: "30px", marginBottom: "20px" }} className={classes.structureImage} src={Figure2} alt="Screenshot of the LoRa to BACnet configuration web interface."/>

          <ol>
            <li>Log into your Allorado Gateway</li>
            <li>Head over to the “Advanced” tab, under LoRa</li>
            <li>Activate “LoRa to BACnet”</li>
            <li>Head over to the newly created “LoRa to BACnet” tab</li>
            <li>Change the default BACnet device ID, network number or port number if desired</li>
            <li>Continue to “Device mapping” and for each device, select which data points you would like to map to BACnet objects.</li>
            <li>The mapper will automatically assign an object name and ID. Open up “Mapper settings” if you would like to manually configure these.</li>
            <li>When you are satisfied with the configuration, click the “Deploy” button to make the changes take effect.</li>
            <li>Verify the resulting object list in the “Object table” tab.</li>
            <li>You will see the newly created objects appear on the BACnet network. This can be easily verified by using a third-party BACnet explorer, like Yabe.</li>
            <li>When a selected LoRa device transmits a new data, head back over to the “Object table” tab and you will see new value under “Present value”. The new value also propagates on the BACnet network.</li>
          </ol>

          <img style={{ marginTop: "0", marginBottom: "20px" }} className={classes.structureImage} src={Figure6} alt="Screenshot of the resulting BACnet object list."/>

          <h3>3. Using the NodeRED node</h3>
          <ol>
            <li>Open NodeRED from the NodeRED tab</li>
            <li>Scroll down in the left pane to the “BACnet device” category</li>
            <li>Drag a “BACnet Objects” node into your flow</li>
            <li>Double click the node to configure it</li>
            <li>Create a BACnet controller and enter a device ID and network number</li>
            <li>Click “Create” to return to the Objects editor</li>
          </ol>

          <img style={{ margin: "0 auto 30px auto", maxHeight: "400px" }} className={classes.structureImage} src={Figure3} alt="Screenshot of NodeRED configuration panel where BACnet objects are defined."/>

          <p>The <b>Objects editor</b> is designed to let you define BACnet objects. It consists of two editors:</p>
          <ul>
            <li>A <b>table</b> editor: simple editor which best assists you in defining objects</li>
            <li>A <b>text</b> editor: more complex editor, allows bulk definition of objects for advanced users</li>
          </ul>
          <p>Object definitions are shared between these two editors. You can define an object in the table editor and see what its definition looks like in the text editor.</p>
          <p>Multiple object types are supported. The types “analog input”, “binary input” and “character string value” are <b>read-only</b>, their value can only be updated from NodeRED, not from the BACnet network. The types “analog value” and “binary value” can be updated both from NodeRED and the external BACnet network. Hence to control a LoRa device from BACnet, you need an analog or binary value.</p>
          <p>Each object has an object ID, which is unique for its object type across all “BACnet Objects” nodes and the web interface. Both analog input 5 and binary input 5 can exist, as object IDs are unique per object type.</p>

          <h4>Updating BACnet objects</h4>
          <p>To update the value or reliability of a BACnet object (and make this new value visible on the BACnet network), a message can be injected into the node on the left side. To update an object, we need:</p>
          <ul>
            <li>The object ID</li>
            <li>The object type (ai, bi, av, bv or csv)</li>
            <li>Either or both of the following:</li>
            <ul style={{marginTop: "10px"}}>
              <li>A new present value (number or string, depending on object type)</li>
              <li>A new reliability (number from 0 to 15)</li>
            </ul>
          </ul>
          <p>The injected message needs to be an array of updates of the following format:</p>
          
          <img style={{ margin: "0 0 30px 0", maxHeight: "150px" }} className={classes.structureImage} src={Figure4} alt="Input message format for the BACnet Objects node."/>
          
          <p>If the message format is incorrect, an error will be displayed in the NodeRED debug console. When the update succeeds, the value change will be visible across the BACnet network. Please refer to our <NavLink className={classes.docLink} to={"/docs/example-flows"}>example flows</NavLink> for a complete example of updating BACnet object values.</p>
          
          <h4>Receiving BACnet updates</h4>
          <p>When a value is updated from the BACnet network, a message is sent out on the right of the node. This message contains the new value, the object ID and object type. A message is only returned in case of an external update, not when injecting an update yourself on the left of the node. The output format is defined as shown below:</p>

          <img style={{ margin: "0 0 30px 0", maxHeight: "95px" }} className={classes.structureImage} src={Figure5} alt="Output message format for the BACnet Objects node."/>

          <h3>4. Useful tips</h3>
          <ul>
            <li>The web interface mapper and the NodeRED nodes are designed to work simultaneously. Each object ID can only be used once per object type, so the automatic mapper carefully avoids object IDs used by NodeRED.</li>
            <li>Use the “Table editor” in NodeRED to intuitively experiment with defining BACnet objects, afterwards use the “Text editor” to define objects in bulk. The table editor is not available when more than 100 objects are defined for interface performance reasons.</li>
          </ul>
        </div>
      </DocPage>
    </>
  );
}

export default DocumentationPage;
